// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — a deal, and the match it belongs to.
//
// ── WHY THE CRATE IS NOT SHUFFLED FROM THE ROUND SEED ────────────────────────────────────────────
//
// The obvious design is to derive the shuffle from `RoundInfo.seed` and let each client simply
// decline to render the other satchel. It is wrong, and not for the reason you would guess: the
// rematch protocol broadcasts that seed to every peer AND every spectator, so a seeded shuffle does
// not leak "your rival's five cards" — it leaks THE ENTIRE FUTURE ORDER OF THE CRATE to everybody.
// In a game whose central decision is whether the ware you are collecting will still be coming, that
// is a far larger cheat than seeing a hand, and it collapses the whole thing. So the host mints the
// order from a seed of its own and never sends it. Tests and the sim pass one explicitly, which is
// how they stay reproducible without the shipped game ever being predictable.
//
// A match is the best of three deals. The tally persists inside one room across rematches, and the
// LOSER of a deal starts the next one — which, with the +2 tare allowance to whoever did not start,
// is the whole of the turn-0 fairness answer.

import { makeRng, newSeed, shuffle } from '@ben-gy/game-engine/rng';
import { buildDeck, type Card } from './wares';
import { deal, dealWinner, other, type FullState, type PublicState, type Seat } from './rules';
import { type Mode } from './modes';

export const DEALS_TO_WIN = 2;
export const MAX_DEALS = 3;

export interface MatchState {
  pennants: [number, number];
  /** Winner of each finished deal, oldest first. */
  results: Seat[];
}

export const newMatch = (): MatchState => ({ pennants: [0, 0], results: [] });

export const matchOver = (m: MatchState): boolean =>
  m.pennants[0] >= DEALS_TO_WIN || m.pennants[1] >= DEALS_TO_WIN || m.results.length >= MAX_DEALS;

export const matchWinner = (m: MatchState): Seat | null => {
  if (m.pennants[0] === m.pennants[1]) return null;
  return m.pennants[0] > m.pennants[1] ? 0 : 1;
};

export function recordDeal(m: MatchState, winner: Seat): void {
  m.pennants[winner]++;
  m.results.push(winner);
}

/**
 * Who moves first. Deal 1 is a seeded coin flip off the SHARED round seed, so no seat id is ever
 * privileged; deals 2 and 3 are started by whoever lost the one before.
 */
export function starterFor(seed: number, m: MatchState): Seat {
  const last = m.results[m.results.length - 1];
  if (last !== undefined) return other(last);
  return (makeRng(`start:${seed}`)() < 0.5 ? 0 : 1) as Seat;
}

export interface OpenOpts {
  readonly mode: Mode;
  readonly dealNo: number;
  readonly starter: Seat;
  /** Omit in the shipped game — a fresh, private, unsendable order is the point. */
  readonly shuffleSeed?: number;
}

export function openDeal(o: OpenOpts): FullState {
  const order: Card[] = shuffle(makeRng(o.shuffleSeed ?? newSeed()), buildDeck());
  return deal(o.mode, order, o.dealNo, o.starter);
}

/** Deal result, once it is over. */
export function finishDeal(p: PublicState): Seat {
  return dealWinner(p);
}
